import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Link,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  Text,
  useDisclosure,
} from "@chakra-ui/react";
import { keepPreviousData } from "@tanstack/react-query";
import Decimal from "decimal.js";
import { add, Dinero } from "dinero.js";
import { getFundFamilyOutstandingFeeChargeEvents } from "features/capital-call/FeeChargeEventSelect";
import { FeeSummaryTable } from "features/capital-call/FeeSummaryTable";
import {
  useFeeBreakDistributionSummaryRetrieveSuspense,
  useFeeBreakDistributionTableRetrieve,
} from "gen/endpoints/distributions/distributions";
import { useAvailableFeeChargeEventsForCallRetrieve } from "gen/endpoints/fee-charge-event-select/fee-charge-event-select";
import { useFeeListSuspense } from "gen/endpoints/fees/fees";
import { FundFamilyUseAllocationTableRow } from "gen/model/fundFamilyUseAllocationTableRow";
import { get } from "lodash-es";
import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { QueryBoundary } from "shared/api/QueryBoundary";
import {
  ChangeTable,
  FundChangeTableData,
} from "shared/components/change/ChangeTable";
import { LoadingState } from "shared/components/empty/LoadingState";
import { TableBox } from "shared/components/table/box/TableBox";
import { TableBoxBody } from "shared/components/table/box/TableBoxBody";
import { TableBoxCollapse } from "shared/components/table/box/TableBoxCollapse";
import { TableBoxHeader } from "shared/components/table/box/TableBoxHeader";
import { useTableBoxMinHeight } from "shared/components/table/box/TableBoxMinHeight";
import { TableBoxTitle } from "shared/components/table/box/TableBoxTitle";
import { DataTable } from "shared/components/table/data/DataTable";
import { DataTableBoxActions } from "shared/components/table/data/DataTableBoxActions";
import { EqualizedViewToggleButton } from "shared/components/toggle/EqualizedViewToggleButton";
import { useRequiredStringParams } from "shared/hooks/useRequiredStringParams";
import { ArrowUpRightIcon } from "shared/icons/ArrowUpRightIcon";
import { tryMoneyFromString, zeroMoney } from "shared/utils/MoneyUtils";

interface FeeBreakAmounts {
  fundFamily: Dinero<Decimal>;
  funds: Record<string, Dinero<Decimal>>;
}

interface FundFamilyDistributionFeeBreakTableProps {
  tableRows: FundFamilyUseAllocationTableRow[];
  closeComponent: () => void;
}

export const FundFamilyDistributionFeeBreakCard: React.FC<
  FundFamilyDistributionFeeBreakTableProps
> = ({ tableRows, closeComponent }) => {
  const { t } = useTranslation(["distribution", "common"]);
  return (
    <Card variant="table">
      <CardHeader>
        <Text>{t("distribution:feeBreak.title")}</Text>
        <Button size="xs" colorScheme="secondary" onClick={closeComponent}>
          {t("common:remove")}
        </Button>
      </CardHeader>
      <CardBody pb="0">
        <QueryBoundary>
          <FundFamilyDistributionFeeBreakCardBody tableRows={tableRows} />
        </QueryBoundary>
      </CardBody>
    </Card>
  );
};

const FundFamilyDistributionFeeBreakCardBody: React.FC<{
  tableRows: FundFamilyUseAllocationTableRow[];
}> = ({ tableRows }) => {
  const { t } = useTranslation(["distribution", "common"]);
  const { fundFamilyId, fundFamilyEventId } = useRequiredStringParams();
  const [isEqualizedView, setIsEqualizedView] = useState<boolean>(false);

  const { data: feeListData } = useFeeListSuspense(fundFamilyId);

  const feeId = feeListData[0]?.id;
  const { data, isFetching } = useFeeBreakDistributionTableRetrieve(
    fundFamilyId,
    fundFamilyEventId,
    {
      fee_id: feeId,
      is_equalized_view: isEqualizedView || undefined,
    },
    {
      query: {
        placeholderData: keepPreviousData,
      },
    },
  );

  const { isOpen, onOpen, onClose } = useDisclosure();

  const summaryData = useFeeBreakDistributionSummaryRetrieveSuspense(
    fundFamilyId,
    fundFamilyEventId,
    {
      fee_id: feeId,
    },
  );

  const minHeight = useTableBoxMinHeight(
    data?.rows.length ?? 0,
    2,
    80, // extra height for the stats
  );

  const feeBreakAmounts = tableRows.reduce<FeeBreakAmounts>(
    (feeBreakAmountsAcc, row) => {
      // gets the accumulated fund family amount
      const accFundFamilyAmount = get(
        feeBreakAmountsAcc,
        "fundFamily",
        zeroMoney(),
      );
      // gets the current row fund family amount
      const rowFundFamilyAmount = tryMoneyFromString(
        get(row.fund_family, feeId, { amount: "0" }).amount,
      );
      // sets the accumulated fund family amount to the sum of the two
      feeBreakAmountsAcc["fundFamily"] = add(
        accFundFamilyAmount,
        rowFundFamilyAmount,
      );

      // sets the accumulated fund amounts
      Object.keys(row.funds).forEach((fundId) => {
        const rowFund = row.funds[fundId];
        // gets the accumulated fund amount
        const accFundAmount = get(
          feeBreakAmountsAcc["funds"],
          fundId,
          zeroMoney(),
        );
        // gets the current row fund amount
        const rowFundAmount = tryMoneyFromString(
          get(rowFund, feeId, { amount: "0" }).amount,
        );
        // sets the accumulated fund amount to the sum of the two
        feeBreakAmountsAcc["funds"][fundId] = add(accFundAmount, rowFundAmount);
      });

      return feeBreakAmountsAcc;
    },
    {
      fundFamily: zeroMoney(),
      funds: {},
    },
  );

  const changeTableData = useMemo<FundChangeTableData>(
    () =>
      summaryData.data.fund_summary_list.reduce<FundChangeTableData>(
        (data, fundSummary) => ({
          ...data,
          [fundSummary.fund_id]: {
            available: tryMoneyFromString(
              fundSummary.total_outstanding_fee_breaks,
            ),
            change: get(
              feeBreakAmounts,
              `funds.${fundSummary.fund_id}`,
              zeroMoney(),
            ),
            fundName: fundSummary.legal_name,
          },
        }),
        {},
      ),
    [summaryData, feeBreakAmounts],
  );

  return (
    <>
      <ChangeTable
        title={t("distribution:feeBreak.feeBreakSummaryByFundEntity")}
        changeLabel={t("distribution:feeBreak.feeBreak")}
        tableData={changeTableData}
        boxProps={{ my: "12px" }}
      />
      <TableBox
        expandedMinHeight={minHeight}
        isFullWidth={true}
        isDefaultExpanded={false}
      >
        <TableBoxHeader>
          <TableBoxTitle>
            {t("distribution:feeBreak.historicalFeeBreakDetails")}
          </TableBoxTitle>
          <DataTableBoxActions>
            <Link onClick={onOpen}>
              {t("distribution:feeBreak.historicalFeeDetailsByInvestor")}
              <ArrowUpRightIcon />
            </Link>
            <EqualizedViewToggleButton
              isEqualizedView={isEqualizedView}
              setIsEqualizedView={setIsEqualizedView}
            />
          </DataTableBoxActions>
        </TableBoxHeader>
        <TableBoxCollapse>
          <TableBoxBody>
            {data ? (
              <DataTable data={data} isLoading={isFetching} />
            ) : (
              <LoadingState />
            )}
          </TableBoxBody>
        </TableBoxCollapse>
      </TableBox>
      {isOpen && feeId && <FeeModal onCloseComplete={onClose} feeId={feeId} />}
    </>
  );
};

const FeeModal: React.FC<{
  onCloseComplete: () => void;
  feeId: string;
}> = ({ onCloseComplete, feeId }) => {
  const { isOpen, onClose } = useDisclosure({ defaultIsOpen: true });
  const { fundFamilyId } = useRequiredStringParams();
  const { t } = useTranslation(["distribution", "common"]);
  const { data: availableEventData } =
    useAvailableFeeChargeEventsForCallRetrieve(
      fundFamilyId,
      {
        fee_id: feeId,
      },
      {
        query: {
          enabled: Boolean(feeId),
        },
      },
    );
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      onCloseComplete={onCloseComplete}
      closeOnEsc={true}
      isCentered={true}
      size="full"
      scrollBehavior="inside"
      closeOnOverlayClick={true}
    >
      <ModalContent>
        <QueryBoundary>
          <ModalBody>
            {availableEventData && (
              <FeeSummaryTable
                feeChargeEventIds={getFundFamilyOutstandingFeeChargeEvents(
                  availableEventData,
                ).map(({ id }) => id)}
                title={t(
                  "distribution:feeBreak.historicalFeeDetailsByInvestor",
                )}
              />
            )}
          </ModalBody>
          <ModalFooter>
            <Button onClick={onClose}>{t("common:close")}</Button>
          </ModalFooter>
        </QueryBoundary>
      </ModalContent>
    </Modal>
  );
};
